import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Box, Typography } from '@mui/material';

const ReCAPTCHAComponent = forwardRef(
  ({ onVerify, onExpire, error, className = '', theme = 'light', size = 'normal' }, ref) => {
    const recaptchaRef = useRef();

    // Get the site key from environment variables
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

    useImperativeHandle(ref, () => ({
      // Method to reset the reCAPTCHA
      reset: () => {
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      },
      // Method to get the current value
      getValue: () => {
        if (recaptchaRef.current) {
          return recaptchaRef.current.getValue();
        }
        return null;
      },
      // Method to execute the reCAPTCHA programmatically
      execute: () => {
        if (recaptchaRef.current) {
          return recaptchaRef.current.execute();
        }
        return null;
      },
    }));

    const handleChange = (token) => {
      if (onVerify) {
        onVerify(token);
      }
    };

    const handleExpired = () => {
      if (onExpire) {
        onExpire();
      }
    };

    if (!siteKey) {
      return (
        <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
          <Typography variant="caption" color="error">
            ReCAPTCHA configuration missing
          </Typography>
        </Box>
      );
    }

    return (
      <Box className={`recaptcha-wrapper ${className}`}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={siteKey}
          onChange={handleChange}
          onExpired={handleExpired}
          theme={theme}
          size={size}
        />
        {error && (
          <Typography className="errorMessage" sx={{ mt: 1 }}>
            <sup>*</sup>
            {error}
          </Typography>
        )}
      </Box>
    );
  }
);

ReCAPTCHAComponent.displayName = 'ReCAPTCHAComponent';

export default ReCAPTCHAComponent;
